import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Target, Sparkles, Shield, Users, TrendingUp, Lock, Leaf } from 'lucide-react';
import { useAuth } from '../App';

const AboutPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-rose-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Heart className="w-12 h-12 text-rose-500" />
            <h1 className="text-4xl sm:text-5xl font-bold gradient-text">About LuvCup</h1>
          </div>
          <p className="text-xl sm:text-2xl text-gray-700 font-medium italic">
            Where Love Meets Insight
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {/* Introduction */}
          <div className="glass-card p-6 sm:p-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              LuvCup isn't just another dating platform — it's a space built to help people understand themselves, connect deeply, and love intentionally. We believe that real connection starts with self-awareness, empathy, and the courage to grow.
            </p>
          </div>

          {/* Mission */}
          <div className="glass-card p-6 sm:p-8">
            <div className="flex items-center space-x-3 mb-4">
              <Target className="w-8 h-8 text-rose-500" />
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Our Mission</h2>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              To help individuals and couples build authentic, emotionally intelligent relationships through reflection, science-based tools, and meaningful communication.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We combine psychology, data, and heart to help you find — and keep — the love that truly fits.
            </p>
          </div>

          {/* What Makes Us Different */}
          <div className="glass-card p-6 sm:p-8">
            <div className="flex items-center space-x-3 mb-6">
              <Sparkles className="w-8 h-8 text-purple-500" />
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">What Makes LuvCup Different</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold">
                  🧭
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Purpose Over Swiping</h3>
                  <p className="text-gray-700">We focus on compatibility and emotional connection — not endless matches.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold">
                  🧠
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Science-Backed Assessments</h3>
                  <p className="text-gray-700">Discover your love language, communication style, and personality insights.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold">
                  💕
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">AI-Powered Compatibility</h3>
                  <p className="text-gray-700">Get personalized matches and relationship feedback that goes beyond surface-level attraction.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold">
                  🌱
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Growth Together</h3>
                  <p className="text-gray-700">Couples can explore shared insights, track progress, and strengthen their bond.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold">
                  🔒
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Safe & Private</h3>
                  <p className="text-gray-700">Your emotional data stays protected. Your heart, respected.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Our Values */}
          <div className="glass-card p-6 sm:p-8">
            <div className="flex items-center space-x-3 mb-6">
              <Leaf className="w-8 h-8 text-green-500" />
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Our Values</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border-l-4 border-rose-500">
                <h3 className="font-bold text-gray-900 mb-2">Authenticity</h3>
                <p className="text-gray-700">Be real. Be you.</p>
              </div>
              <div className="bg-white p-4 rounded-lg border-l-4 border-purple-500">
                <h3 className="font-bold text-gray-900 mb-2">Empathy</h3>
                <p className="text-gray-700">Understand others through compassion.</p>
              </div>
              <div className="bg-white p-4 rounded-lg border-l-4 border-pink-500">
                <h3 className="font-bold text-gray-900 mb-2">Growth</h3>
                <p className="text-gray-700">Love is a lifelong learning process.</p>
              </div>
              <div className="bg-white p-4 rounded-lg border-l-4 border-blue-500">
                <h3 className="font-bold text-gray-900 mb-2">Respect</h3>
                <p className="text-gray-700">Every user deserves safety, trust, and dignity.</p>
              </div>
            </div>
          </div>

          {/* Vision */}
          <div className="glass-card p-6 sm:p-8">
            <div className="flex items-center space-x-3 mb-4">
              <TrendingUp className="w-8 h-8 text-blue-500" />
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Our Vision</h2>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed">
              To redefine how people experience love — from the first connection to lifelong partnership — with technology that empowers emotional growth and meaningful human connection.
            </p>
          </div>

          {/* Call to Action */}
          <div className="glass-card p-6 sm:p-8 bg-gradient-to-br from-rose-100 to-purple-100 border-2 border-rose-300">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Heart className="w-8 h-8 text-rose-600" />
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Join the Movement</h2>
              </div>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Whether you're single, dating, or rediscovering love, LuvCup is here to help you understand yourself, connect deeper, and love better.
              </p>
              <button
                onClick={() => navigate(user ? '/personalized' : '/auth')}
                className="bg-gradient-to-r from-rose-500 to-purple-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all transform hover:scale-105"
              >
                Start Your Journey Today
              </button>
              <p className="text-gray-600 mt-6 italic">
                — because love isn't luck. It's awareness.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
